// bluaIQ — Documents Module
// Official project documents: permits, plans, surveys, inspections, HOA, lien waivers, warranties

import { apiFetch } from '../utils/api'
import type { ActorLabel } from '../shared/actorLabel'
import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  FolderOpen, Upload, FileText, Search, Filter, Calendar,
  CheckCircle2, Clock, XCircle, AlertTriangle, Download, Trash2, Eye,
  // Stamp: permit workflow handoff banner icon (Documents/Permits UX
  // handoff PR). Companion arrow icon removed alongside the dead-end
  // CTA in fix/documents-permits-cta (Permits module not yet live).
  Stamp,
} from 'lucide-react'
import { isDemoProject } from '../utils/demoData'
import SavedProjectOnlyNotice from './SavedProjectOnlyNotice'

// ── TYPES ─────────────────────────────────────────

interface ProjectDocument {
  id: string
  name: string
  category: string
  status: string
  issueDate?: string
  expiryDate?: string
  storagePath?: string
  fileSize?: number
  mimeType?: string
  notes?: string
  // Server resolves the uploader to a client-safe label; the raw Clerk id is no
  // longer on the wire. Not rendered today, but typed to the real shape so a
  // future render can only use the safe label. See src/shared/actorLabel.ts.
  uploadedByLabel?: ActorLabel | null
  createdAt: string
}

function buildDocumentFileUrl(projectId: string, documentId: string, disposition: 'inline' | 'attachment' = 'inline') {
  const params = disposition === 'attachment' ? '?disposition=attachment' : ''
  return `/api/projects/${projectId}/documents/${documentId}/file${params}`
}

// ── CONFIG ────────────────────────────────────────

const CATEGORIES: Record<string, { en: string; es: string; color: string; bg: string }> = {
  PERMIT:      { en: 'Permit',       es: 'Permiso',          color: 'text-blue-700',   bg: 'bg-blue-50 border-blue-200' },
  PLAN:        { en: 'Plan',         es: 'Plano',            color: 'text-purple-700', bg: 'bg-purple-50 border-purple-200' },
  SURVEY:      { en: 'Survey',       es: 'Levantamiento',    color: 'text-cyan-700',   bg: 'bg-cyan-50 border-cyan-200' },
  INSPECTION:  { en: 'Inspection',   es: 'Inspección',       color: 'text-amber-700',  bg: 'bg-amber-50 border-amber-200' },
  HOA:         { en: 'HOA',          es: 'HOA',              color: 'text-pink-700',   bg: 'bg-pink-50 border-pink-200' },
  LIEN_WAIVER: { en: 'Lien Waiver',  es: 'Waiver de Lien',   color: 'text-emerald-700',bg: 'bg-emerald-50 border-emerald-200' },
  WARRANTY:    { en: 'Warranty',     es: 'Garantía',         color: 'text-orange-700', bg: 'bg-orange-50 border-orange-200' },
  OTHER:       { en: 'Other',        es: 'Otro',             color: 'text-gray-700',   bg: 'bg-gray-50 border-gray-200' },
}

const STATUS_CONFIG: Record<string, { en: string; es: string; icon: any; color: string; bg: string }> = {
  APPROVED: { en: 'Approved', es: 'Aprobado',  icon: CheckCircle2,  color: 'text-emerald-600', bg: 'bg-emerald-50' },
  PENDING:  { en: 'Pending',  es: 'Pendiente', icon: Clock,         color: 'text-amber-600',   bg: 'bg-amber-50' },
  EXPIRED:  { en: 'Expired',  es: 'Vencido',   icon: AlertTriangle, color: 'text-red-600',     bg: 'bg-red-50' },
  REJECTED: { en: 'Rejected', es: 'Rechazado', icon: XCircle,       color: 'text-red-600',     bg: 'bg-red-50' },
}

// ── DEMO DATA ─────────────────────────────────────

const DEMO_DOCUMENTS: ProjectDocument[] = [
  {
    id: 'doc-1', name: 'Building Permit #BP-2026-4521', category: 'PERMIT', status: 'APPROVED',
    issueDate: '2026-02-28', expiryDate: '2027-02-28', notes: 'City of Frisco — Residential New Construction',
    uploadedByLabel: { kind: 'team_member' }, createdAt: '2026-02-28T00:00:00Z', mimeType: 'application/pdf', fileSize: 245000,
  },
  {
    id: 'doc-2', name: 'Architectural Plans v3 — 4521 Legacy Dr', category: 'PLAN', status: 'APPROVED',
    issueDate: '2026-02-01', notes: 'Chen Architecture — Final revision approved by city',
    uploadedByLabel: { kind: 'team_member' }, createdAt: '2026-02-01T00:00:00Z', mimeType: 'application/pdf', fileSize: 8500000,
  },
  {
    id: 'doc-3', name: 'Structural Engineering Report', category: 'PLAN', status: 'APPROVED',
    issueDate: '2026-02-15', notes: 'Slab-on-grade foundation design per IBC 2021',
    uploadedByLabel: { kind: 'team_member' }, createdAt: '2026-02-15T00:00:00Z', mimeType: 'application/pdf', fileSize: 3200000,
  },
  {
    id: 'doc-4', name: 'Soil Test Report — Geotechnical', category: 'SURVEY', status: 'APPROVED',
    issueDate: '2026-01-22', notes: 'Expansive clay soil — PI=32. Requires moisture conditioning.',
    uploadedByLabel: { kind: 'team_member' }, createdAt: '2026-01-22T00:00:00Z', mimeType: 'application/pdf', fileSize: 1800000,
  },
  {
    id: 'doc-5', name: 'ALTA Survey — Lot 14, Legacy Heights', category: 'SURVEY', status: 'APPROVED',
    issueDate: '2026-01-24', notes: 'Boundary survey with easements and setbacks',
    uploadedByLabel: { kind: 'team_member' }, createdAt: '2026-01-24T00:00:00Z', mimeType: 'application/pdf', fileSize: 4100000,
  },
  {
    id: 'doc-6', name: 'Framing Inspection — Passed', category: 'INSPECTION', status: 'APPROVED',
    issueDate: '2026-03-15', notes: 'Inspector Rosa Gutierrez — All framing per approved plans',
    uploadedByLabel: { kind: 'team_member' }, createdAt: '2026-03-15T00:00:00Z', mimeType: 'image/jpeg', fileSize: 520000,
  },
  {
    id: 'doc-7', name: 'Plumbing Rough-In Inspection', category: 'INSPECTION', status: 'PENDING',
    notes: 'Scheduled for March 28 — Torres Plumbing',
    uploadedByLabel: { kind: 'team_member' }, createdAt: '2026-03-20T00:00:00Z',
  },
  {
    id: 'doc-8', name: 'HOA Architectural Review Approval', category: 'HOA', status: 'APPROVED',
    issueDate: '2026-01-30', expiryDate: '2026-07-30', notes: 'Legacy Heights HOA — Exterior colors and materials approved',
    uploadedByLabel: { kind: 'team_member' }, createdAt: '2026-01-30T00:00:00Z', mimeType: 'application/pdf', fileSize: 180000,
  },
  {
    id: 'doc-9', name: 'Foundation Lien Waiver — Solid Ground LLC', category: 'LIEN_WAIVER', status: 'APPROVED',
    issueDate: '2026-03-05', notes: 'Conditional waiver — $18,500 foundation pour',
    uploadedByLabel: { kind: 'team_member' }, createdAt: '2026-03-05T00:00:00Z', mimeType: 'application/pdf', fileSize: 95000,
  },
  {
    id: 'doc-10', name: 'Electrical Permit', category: 'PERMIT', status: 'PENDING',
    notes: 'Submitted to City of Frisco — Awaiting approval',
    uploadedByLabel: { kind: 'team_member' }, createdAt: '2026-03-18T00:00:00Z',
  },
]

// ── COMPONENT ─────────────────────────────────────

export default function DocumentsModule() {
  const { getToken } = useAuth()
  const { projectId } = useParams()
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const isEs = i18n.language === 'es'

  const [uploadToken, setUploadToken] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [filterCategory, setFilterCategory] = useState<string | null>(null)
  const [filterStatus, setFilterStatus] = useState<string | null>(null)
  const [showAddForm, setShowAddForm] = useState(false)

  useEffect(() => {
    let alive = true
    getToken()
      .then((token) => { if (alive) setUploadToken(token || null) })
      .catch(() => { if (alive) setUploadToken(null) })
    return () => { alive = false }
  }, [getToken])
  // Sidebar bilingual + Documents/Permits handoff PR: the new-doc
  // form default category is `PLAN` (the most common upload), not
  // `PERMIT`. Permit-oriented workflows now live in the dedicated
  // Permits & Local Review module — Documents keeps storage support
  // for permit files but does not pre-select PERMIT as the default.
  // See docs/BLUAIQ_WEBSITE/architecture_principles.md §3.1.
  const [newDoc, setNewDoc] = useState({ name: '', category: 'PLAN', customCategory: '', status: 'PENDING', notes: '', issueDate: '', expiryDate: '' })
  const [pickedFile, setPickedFile] = useState<File | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const formFileRef = useRef<HTMLInputElement>(null)

  const { data: apiDocs, isLoading } = useQuery<ProjectDocument[]>({
    queryKey: ['documents', projectId],
    queryFn: async () => { const token = await getToken(); return apiFetch(`/api/projects/${projectId}/documents`, { headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } }).then(r => {
      if (!r.ok) throw new Error('API unavailable')
      return r.json()
    }) },
    retry: false,
  })

  const isDemo = isDemoProject(projectId)
  const documents = Array.isArray(apiDocs) ? apiDocs : (isDemo ? DEMO_DOCUMENTS : [])

  // fix/documents-quick-upload: the one-click quick-upload mutation
  // (hardcoded category: OTHER, status: PENDING, raw filename) was removed.
  // All uploads now go through the form so category, status, dates, and notes
  // are always captured — important for permits, inspections, and expiry tracking.

  // Form upload: file + metadata from the form (name, category, status, dates, notes).
  // Falls back to JSON-only POST when no file is picked (metadata-only document).
  const addDocMutation = useMutation({
    mutationFn: async (doc: typeof newDoc) => {
      const token = await getToken()
      const category = doc.category === 'CUSTOM' ? doc.customCategory : doc.category

      if (pickedFile) {
        const formData = new FormData()
        formData.append('file', pickedFile)
        formData.append('name', doc.name || pickedFile.name)
        formData.append('category', category)
        formData.append('status', doc.status)
        if (doc.notes) formData.append('notes', doc.notes)
        if (doc.issueDate) formData.append('issueDate', doc.issueDate)
        if (doc.expiryDate) formData.append('expiryDate', doc.expiryDate)
        const res = await apiFetch(`/api/projects/${projectId}/documents/upload`, {
          method: 'POST',
          headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
          body: formData,
        })
        if (!res.ok) {
          const err = await res.json().catch(() => ({}))
          throw new Error(err.error || err.detail || `Upload failed (${res.status})`)
        }
        return res.json()
      }

      // Metadata-only document (no file attached)
      const res = await apiFetch(`/api/projects/${projectId}/documents`, {
        method: 'POST',
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: doc.name,
          category,
          status: doc.status,
          notes: doc.notes || undefined,
          issueDate: doc.issueDate ? new Date(doc.issueDate).toISOString() : undefined,
          expiryDate: doc.expiryDate ? new Date(doc.expiryDate).toISOString() : undefined,
        }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.error || err.detail || `Save failed (${res.status})`)
      }
      return res.json()
    },
    onSuccess: () => {
      setUploadError(null)
      queryClient.invalidateQueries({ queryKey: ['documents', projectId] })
      setShowAddForm(false)
      setPickedFile(null)
      // Reset restores the PLAN default — PERMIT here was a leftover from before
      // the Permits & Local Review separation; the initial state was migrated to
      // PLAN but this reset site was missed (invisible behind a >=2 count that
      // demo rows satisfied). Census step 7 exposed it.
      setNewDoc({ name: '', category: 'PLAN', customCategory: '', status: 'PENDING', notes: '', issueDate: '', expiryDate: '' })
    },
    onError: (err: Error) => setUploadError(err.message),
  })

  // Toast for action feedback (view/download errors + status
  // updates). Auto-dismisses after 3 s so it doesn't linger.
  const [actionToast, setActionToast] = useState<{ msg: string; tone: 'ok' | 'err' } | null>(null)
  const showToast = (msg: string, tone: 'ok' | 'err' = 'ok') => {
    setActionToast({ msg, tone })
    setTimeout(() => setActionToast(null), 3000)
  }

  // Authenticated file fetch helper. Anchor-href view/download was
  // the root cause of the production "Missing authentication
  // token" page: anchors navigate the browser to the API URL
  // WITHOUT the Clerk Bearer token (the token only lives in JS).
  // This helper fetches the bytes with the token attached, then
  // hands back a Blob URL the caller can open or download.
  const fetchDocumentBlob = async (
    documentId: string,
    disposition: 'inline' | 'attachment',
  ): Promise<{ blobUrl: string; mimeType: string }> => {
    const token = await getToken()
    const query = disposition === 'attachment' ? '?disposition=attachment' : ''
    const res = await apiFetch(`/api/projects/${projectId}/documents/${documentId}/file${query}`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.error || err.detail || `HTTP ${res.status}`)
    }
    const blob = await res.blob()
    return { blobUrl: URL.createObjectURL(blob), mimeType: blob.type }
  }

  const handleViewDocument = async (doc: ProjectDocument) => {
    if (!doc.storagePath) return
    try {
      const { blobUrl } = await fetchDocumentBlob(doc.id, 'inline')
      const win = window.open(blobUrl, '_blank', 'noopener,noreferrer')
      // Popup blocked → fall back to download anchor so user
      // still gets the file. Revoke the URL after a beat so the
      // download has time to start.
      if (!win) {
        const a = document.createElement('a')
        a.href = blobUrl
        a.download = doc.name
        document.body.appendChild(a)
        a.click()
        a.remove()
      }
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000)
    } catch (err: any) {
      showToast(
        isEs ? 'No se pudo abrir el documento' : 'Could not open document',
        'err',
      )
    }
  }

  const handleDownloadDocument = async (doc: ProjectDocument) => {
    if (!doc.storagePath) return
    try {
      const { blobUrl } = await fetchDocumentBlob(doc.id, 'attachment')
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = doc.name
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000)
    } catch (err: any) {
      showToast(
        isEs ? 'No se pudo descargar el documento' : 'Could not download document',
        'err',
      )
    }
  }

  // PATCH document status (PENDING / APPROVED / REJECTED /
  // EXPIRED). Only the status field rides this path — the new
  // backend route's schema rejects anything else, so the FE
  // payload stays minimal. Query invalidation uses the same
  // key the list query reads so the badge + counts refresh
  // together.
  const statusMutation = useMutation({
    mutationFn: async ({ docId, status }: { docId: string; status: string }) => {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/documents/${docId}`, {
        method: 'PATCH',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.error || err.detail || `HTTP ${res.status}`)
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', projectId] })
      showToast(isEs ? 'Estado actualizado' : 'Status updated', 'ok')
    },
    onError: () => {
      showToast(
        isEs ? 'No se pudo actualizar el estado' : 'Could not update status',
        'err',
      )
    },
  })

  // Filters
  const filtered = documents.filter(doc => {
    const matchSearch = !search ||
      doc.name.toLowerCase().includes(search.toLowerCase()) ||
      doc.notes?.toLowerCase().includes(search.toLowerCase())
    const matchCategory = !filterCategory || doc.category === filterCategory
    const matchStatus = !filterStatus || doc.status === filterStatus
    return matchSearch && matchCategory && matchStatus
  })

  // Group by category for the summary
  const categoryCounts = documents.reduce((acc, doc) => {
    acc[doc.category] = (acc[doc.category] || 0) + 1
    return acc
  }, {} as Record<string, number>)

  const statusCounts = documents.reduce((acc, doc) => {
    acc[doc.status] = (acc[doc.status] || 0) + 1
    return acc
  }, {} as Record<string, number>)

  if (isLoading) return <div className="p-5 animate-pulse"><div className="h-64 bg-[#ECEAE4] rounded-xl" /></div>

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return ''
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—'
    return new Date(dateStr).toLocaleDateString(isEs ? 'es-MX' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const isExpiringSoon = (expiryDate?: string) => {
    if (!expiryDate) return false
    const diff = new Date(expiryDate).getTime() - Date.now()
    return diff > 0 && diff < 30 * 24 * 60 * 60 * 1000 // 30 days
  }

  return (
    <div className="p-5 max-w-5xl mx-auto space-y-4 relative">

      {/* Toast — view/download errors + status-update confirms.
          Lives at the top of the module so the user sees the
          result of every action without scrolling. */}
      {actionToast && (
        <div
          className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 text-white text-[13px] font-[800] px-4 py-2.5 rounded-full shadow-lg ${
            actionToast.tone === 'err' ? 'bg-red-600' : 'bg-[#1C1A17]'
          }`}
          role="status"
        >
          {actionToast.msg}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[18px] font-[900] tracking-[-0.5px] text-[#1C1A17]">
            {isEs ? 'Documentos' : 'Documents'}
          </h1>
          <p className="text-[11px] font-[600] text-[#9E9890] mt-0.5">
            {documents.length} {isEs ? 'documentos' : 'documents'} · {statusCounts['PENDING'] || 0} {isEs ? 'pendientes' : 'pending'}
          </p>
        </div>
        {/* fix/documents-quick-upload: single upload action. The previous
            one-click ghost button bypassed metadata (category: OTHER, status:
            PENDING, raw filename). All uploads now use this form button so
            type, status, dates, and notes are always set correctly.
            !isDemo guard keeps upload controls off demo projects (PR #138A). */}
        {!isDemo && (
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex items-center gap-1.5 bg-[#06539B] text-white text-[13px] font-[800] px-4 py-2.5 rounded-full hover:bg-[#14457E] transition-colors"
          >
            <Upload size={14} />
            {isEs ? 'Subir Documento' : 'Upload Document'}
          </button>
        )}
      </div>

      {/* PR #138A: demo guard. Banner sits below the header so demo
          users still see the DEMO_DOCUMENTS list (preserves demo
          experience) but cannot trigger any real upload. */}
      {isDemo && (
        <SavedProjectOnlyNotice
          testId="documents-upload-demo-notice"
          bodyEn="Document uploads are available in saved projects. Create or open a real project to add documents."
          bodyEs="La carga de documentos está disponible en proyectos guardados. Crea o abre un proyecto real para agregar documentos."
        />
      )}

      {/* Add document form — never mounts in demo (PR #138A) */}
      {!isDemo && showAddForm && (
        <div className="bg-white border border-[#ECEAE4] rounded-xl p-5 space-y-4">
          <h3 className="text-[14px] font-[800] text-[#1C1A17]">
            {isEs ? 'Subir documento' : 'Upload document'}
          </h3>

          {/* File picker */}
          <div>
            <label className="text-[10px] font-[800] text-[#9E9890] uppercase tracking-[0.5px]">
              {isEs ? 'Archivo' : 'File'} <span className="text-[#9E9890] font-[600] normal-case tracking-normal">({isEs ? 'PDF, imagen, Word — máx 25MB' : 'PDF, image, Word — 25MB max'})</span>
            </label>
            <div className="mt-1 flex items-center gap-2">
              <input
                ref={formFileRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={(e) => {
                  const f = e.target.files?.[0] || null
                  setPickedFile(f)
                  if (f && !newDoc.name) setNewDoc({ ...newDoc, name: f.name.replace(/\.\w+$/, '') })
                }}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => formFileRef.current?.click()}
                className="flex items-center gap-1.5 border border-[#ECEAE4] text-[#1C1A17] text-[12px] font-[700] px-3 py-2 rounded-lg hover:bg-[#F2F0EB] transition-colors"
              >
                <Upload size={13} />
                {pickedFile ? (isEs ? 'Cambiar archivo' : 'Change file') : (isEs ? 'Seleccionar archivo' : 'Choose file')}
              </button>
              {pickedFile && (
                <span className="text-[11px] font-[600] text-[#1C1A17] truncate">
                  {pickedFile.name} · {formatFileSize(pickedFile.size)}
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-[800] text-[#9E9890] uppercase tracking-[0.5px]">
                {isEs ? 'Nombre del documento' : 'Document name'} *
              </label>
              <input
                type="text"
                value={newDoc.name}
                onChange={e => setNewDoc({ ...newDoc, name: e.target.value })}
                placeholder={isEs ? 'Ej: Permiso de construcción' : 'e.g. Building Permit'}
                className="mt-1 w-full px-3 py-2.5 text-[14px] font-[600] border border-[#ECEAE4] rounded-lg focus:outline-none focus:border-[#06539B] focus:ring-1 focus:ring-[#06539B]/20"
              />
            </div>
            <div>
              <label className="text-[10px] font-[800] text-[#9E9890] uppercase tracking-[0.5px]">
                {isEs ? 'Tipo de documento' : 'Document type'}
              </label>
              <select
                value={newDoc.category}
                onChange={e => setNewDoc({ ...newDoc, category: e.target.value })}
                className="mt-1 w-full px-3 py-2.5 text-[14px] font-[600] border border-[#ECEAE4] rounded-lg focus:outline-none focus:border-[#06539B] bg-white"
              >
                {Object.entries(CATEGORIES).map(([key, cat]) => (
                  <option key={key} value={key}>{isEs ? cat.es : cat.en}</option>
                ))}
                <option value="CUSTOM">{isEs ? '✏️ Escribir otro tipo...' : '✏️ Custom type...'}</option>
              </select>
            </div>
          </div>

          {/* Sidebar bilingual + Documents/Permits handoff PR:
              contextual handoff banner that appears when the user
              selects PERMIT as the document type. Documents keeps the
              file (storage/library role), but the permit *workflow*
              (status, dates, blockers, inspections, fees, expiration,
              local review) lives in the separate Permits & Local
              Review module. The banner is bilingual, presentational
              only — it does NOT block upload, does NOT migrate data,
              does NOT add backend entities. It just makes the
              architectural separation visible in the UI.
              See docs/BLUAIQ_WEBSITE/architecture_principles.md §3.1. */}
          {newDoc.category === 'PERMIT' && (
            <div
              data-testid="documents-permit-workflow-handoff"
              className="rounded-lg border border-[#06539B]/20 bg-[#06539B]/5 p-3 flex items-start gap-2.5"
            >
              <Stamp size={16} className="text-[#06539B] flex-shrink-0 mt-0.5" />
              <div className="flex-1 text-[12px] leading-relaxed">
                <div className="font-[700] text-[#1C1A17] mb-0.5">
                  {isEs
                    ? 'Este archivo se guarda en Documentos. El flujo del permiso vive en Permisos y Revisión Local.'
                    : 'This file is saved in Documents. The permit workflow lives in Permits & Local Review.'}
                </div>
                <div className="text-[#5E5A52]">
                  {isEs
                    ? 'Documentos guarda el archivo. Permisos y Revisión Local lleva el estado, fechas, correcciones, inspecciones y vencimientos.'
                    : 'Documents stores the file. Permits & Local Review tracks status, dates, corrections, inspections, and expiration.'}
                </div>
                {/* CTA link removed (fix/documents-permits-cta): Permits &
                    Local Review is ComingSoonPlaceholder — not yet functional.
                    The body copy above communicates the architectural separation
                    without directing users into a dead-end route. Re-add this
                    link when the module ships and is restored to the sidebar. */}
              </div>
            </div>
          )}

          {/* Custom category input */}
          {newDoc.category === 'CUSTOM' && (
            <div>
              <label className="text-[10px] font-[800] text-[#9E9890] uppercase tracking-[0.5px]">
                {isEs ? 'Tipo personalizado' : 'Custom type'}
              </label>
              <input
                type="text"
                value={newDoc.customCategory}
                onChange={e => setNewDoc({ ...newDoc, customCategory: e.target.value })}
                placeholder={isEs ? 'Ej: Carta de crédito, Fianza, Plano eléctrico...' : 'e.g. Letter of credit, Bond, Electrical plan...'}
                className="mt-1 w-full px-3 py-2.5 text-[14px] font-[600] border border-[#ECEAE4] rounded-lg focus:outline-none focus:border-[#06539B] focus:ring-1 focus:ring-[#06539B]/20"
              />
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] font-[800] text-[#9E9890] uppercase tracking-[0.5px]">
                {isEs ? 'Estado' : 'Status'}
              </label>
              <select
                value={newDoc.status}
                onChange={e => setNewDoc({ ...newDoc, status: e.target.value })}
                className="mt-1 w-full px-3 py-2.5 text-[14px] font-[600] border border-[#ECEAE4] rounded-lg focus:outline-none focus:border-[#06539B] bg-white"
              >
                {Object.entries(STATUS_CONFIG).map(([key, s]) => (
                  <option key={key} value={key}>{isEs ? s.es : s.en}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-[800] text-[#9E9890] uppercase tracking-[0.5px]">
                {isEs ? 'Fecha de emisión' : 'Issue date'}
              </label>
              <input
                type="date"
                value={newDoc.issueDate}
                onChange={e => setNewDoc({ ...newDoc, issueDate: e.target.value })}
                className="mt-1 w-full px-3 py-2.5 text-[14px] font-[600] border border-[#ECEAE4] rounded-lg focus:outline-none focus:border-[#06539B]"
              />
            </div>
            <div>
              <label className="text-[10px] font-[800] text-[#9E9890] uppercase tracking-[0.5px]">
                {isEs ? 'Fecha de vencimiento' : 'Expiry date'}
              </label>
              <input
                type="date"
                value={newDoc.expiryDate}
                onChange={e => setNewDoc({ ...newDoc, expiryDate: e.target.value })}
                className="mt-1 w-full px-3 py-2.5 text-[14px] font-[600] border border-[#ECEAE4] rounded-lg focus:outline-none focus:border-[#06539B]"
              />
            </div>
          </div>

          <div>
            <label className="text-[10px] font-[800] text-[#9E9890] uppercase tracking-[0.5px]">
              {isEs ? 'Notas' : 'Notes'}
            </label>
            <textarea
              value={newDoc.notes}
              onChange={e => setNewDoc({ ...newDoc, notes: e.target.value })}
              placeholder={isEs ? 'Detalles opcionales...' : 'Optional details...'}
              rows={2}
              className="mt-1 w-full px-3 py-2.5 text-[14px] font-[600] border border-[#ECEAE4] rounded-lg focus:outline-none focus:border-[#06539B] focus:ring-1 focus:ring-[#06539B]/20 resize-none"
            />
          </div>

          {uploadError && (
            <div className="text-[11px] font-[700] text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              {uploadError}
            </div>
          )}

          <div className="flex items-center gap-2 justify-end">
            <button
              onClick={() => { setShowAddForm(false); setPickedFile(null); setUploadError(null) }}
              className="text-[11px] font-[700] text-[#9E9890] px-4 py-2 rounded-full hover:text-[#1C1A17] transition-colors"
            >
              {isEs ? 'Cancelar' : 'Cancel'}
            </button>
            <button
              onClick={() => newDoc.name && addDocMutation.mutate(newDoc)}
              disabled={addDocMutation.isPending || !newDoc.name || (newDoc.category === 'CUSTOM' && !newDoc.customCategory)}
              className="text-[11px] font-[800] text-white bg-[#06539B] px-5 py-2.5 rounded-full hover:bg-[#14457E] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {addDocMutation.isPending
                ? (isEs ? 'Guardando…' : 'Saving…')
                : pickedFile
                  ? (isEs ? 'Subir y guardar' : 'Upload & save')
                  : (isEs ? 'Guardar documento' : 'Save document')}
            </button>
          </div>
        </div>
      )}

      {/* Status summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard
          label={isEs ? 'Total' : 'Total'}
          value={documents.length}
          icon={FolderOpen}
          color="#1C1A17"
          onClick={() => { setFilterStatus(null); setFilterCategory(null) }}
          active={!filterStatus}
        />
        <SummaryCard
          label={isEs ? 'Aprobados' : 'Approved'}
          value={statusCounts['APPROVED'] || 0}
          icon={CheckCircle2}
          color="#22C55E"
          onClick={() => setFilterStatus(filterStatus === 'APPROVED' ? null : 'APPROVED')}
          active={filterStatus === 'APPROVED'}
        />
        <SummaryCard
          label={isEs ? 'Pendientes' : 'Pending'}
          value={statusCounts['PENDING'] || 0}
          icon={Clock}
          color="#F59E0B"
          onClick={() => setFilterStatus(filterStatus === 'PENDING' ? null : 'PENDING')}
          active={filterStatus === 'PENDING'}
        />
        <SummaryCard
          label={isEs ? 'Vencidos' : 'Expired'}
          value={statusCounts['EXPIRED'] || 0}
          icon={AlertTriangle}
          color="#EF4444"
          onClick={() => setFilterStatus(filterStatus === 'EXPIRED' ? null : 'EXPIRED')}
          active={filterStatus === 'EXPIRED'}
        />
      </div>

      {/* Search + category filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9E9890]" />
          <input
            type="text"
            placeholder={isEs ? 'Buscar documentos...' : 'Search documents...'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 text-[14px] font-[600] border border-[#ECEAE4] rounded-xl bg-white focus:outline-none focus:border-[#06539B] focus:ring-1 focus:ring-[#06539B]/20 transition-all"
          />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <FilterPill
            label={isEs ? 'Todos' : 'All'}
            active={!filterCategory}
            onClick={() => setFilterCategory(null)}
          />
          {Object.entries(CATEGORIES).map(([key, cat]) => (
            categoryCounts[key] ? (
              <FilterPill
                key={key}
                label={isEs ? cat.es : cat.en}
                active={filterCategory === key}
                onClick={() => setFilterCategory(filterCategory === key ? null : key)}
                count={categoryCounts[key]}
              />
            ) : null
          ))}
        </div>
      </div>

      {/* Documents list */}
      {filtered.length === 0 ? (
        <div className="text-center py-16">
          <FolderOpen size={40} className="mx-auto text-[#ECEAE4] mb-3" />
          <p className="text-[13px] font-[700] text-[#9E9890]">
            {isEs ? 'No hay documentos' : 'No documents found'}
          </p>
          <p className="text-[11px] font-[500] text-[#9E9890] mt-1">
            {isEs ? 'Sube permisos, planos e inspecciones' : 'Upload permits, plans, and inspections'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(doc => {
            const cat = CATEGORIES[doc.category] || CATEGORIES.OTHER
            const status = STATUS_CONFIG[doc.status] || STATUS_CONFIG.PENDING
            const StatusIcon = status.icon
            const expiringSoon = isExpiringSoon(doc.expiryDate)

            return (
              <div
                key={doc.id}
                className="group bg-white border border-[#ECEAE4] rounded-xl p-4 hover:border-[#06539B]/20 hover:shadow-md hover:shadow-[#06539B]/5 transition-all duration-300"
              >
                <div className="flex items-start gap-3">
                  {/* File icon */}
                  <div className={`w-[40px] h-[40px] rounded-lg border flex items-center justify-center flex-shrink-0 ${cat.bg}`}>
                    <FileText size={18} className={cat.color} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-[13px] font-[800] text-[#1C1A17] truncate">{doc.name}</h3>
                      {/* Category badge */}
                      <span className={`text-[9px] font-[800] px-2 py-0.5 rounded-full border ${cat.bg} ${cat.color}`}>
                        {isEs ? cat.es : cat.en}
                      </span>
                      {/* Status badge */}
                      <span className={`inline-flex items-center gap-1 text-[9px] font-[800] px-2 py-0.5 rounded-full ${status.bg} ${status.color}`}>
                        <StatusIcon size={10} />
                        {isEs ? status.es : status.en}
                      </span>
                      {/* Expiring soon warning */}
                      {expiringSoon && (
                        <span className="inline-flex items-center gap-1 text-[9px] font-[800] px-2 py-0.5 rounded-full bg-amber-50 text-amber-600">
                          <AlertTriangle size={10} />
                          {isEs ? 'Vence pronto' : 'Expiring soon'}
                        </span>
                      )}
                    </div>

                    {/* Notes */}
                    {doc.notes && (
                      <p className="text-[11px] font-[500] text-[#9E9890] mt-1 line-clamp-1">{doc.notes}</p>
                    )}

                    {/* Meta row */}
                    <div className="flex items-center gap-4 mt-2 flex-wrap">
                      {doc.issueDate && (
                        <span className="flex items-center gap-1 text-[10px] font-[600] text-[#9E9890]">
                          <Calendar size={10} />
                          {isEs ? 'Emitido' : 'Issued'}: {formatDate(doc.issueDate)}
                        </span>
                      )}
                      {doc.expiryDate && (
                        <span className={`flex items-center gap-1 text-[10px] font-[600] ${expiringSoon ? 'text-amber-600' : 'text-[#9E9890]'}`}>
                          <Clock size={10} />
                          {isEs ? 'Vence' : 'Expires'}: {formatDate(doc.expiryDate)}
                        </span>
                      )}
                      {doc.fileSize && (
                        <span className="text-[10px] font-[600] text-[#9E9890]">
                          {formatFileSize(doc.fileSize)}
                        </span>
                      )}
                      {doc.mimeType && (
                        <span className="text-[10px] font-[600] text-[#9E9890] uppercase">
                          {doc.mimeType.split('/')[1]?.replace('jpeg', 'jpg') || ''}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions — always visible (no hover gate) so
                      mobile users can act. View/Download fetch the
                      file with the Bearer token, build a Blob URL,
                      and either open or anchor-download. The
                      previous anchor-href approach navigated
                      without the token and hit
                      `Missing authentication token` on the API. */}
                  <div className="flex items-center gap-1">
                    <select
                      value={doc.status}
                      onChange={(e) => {
                        const next = e.target.value
                        if (next === doc.status) return
                        statusMutation.mutate({ docId: doc.id, status: next })
                      }}
                      aria-label={isEs ? 'Cambiar estado' : 'Change status'}
                      className="text-[10px] font-[700] border border-[#ECEAE4] rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:border-[#06539B]"
                    >
                      <option value="PENDING">{isEs ? 'Marcar pendiente' : 'Mark pending'}</option>
                      <option value="APPROVED">{isEs ? 'Marcar aprobado' : 'Mark approved'}</option>
                      <option value="REJECTED">{isEs ? 'Marcar rechazado' : 'Mark rejected'}</option>
                      <option value="EXPIRED">{isEs ? 'Marcar vencido' : 'Mark expired'}</option>
                    </select>
                    {doc.storagePath && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleViewDocument(doc)
                        }}
                        className="p-2 rounded-lg hover:bg-[#F2F0EB] transition-colors"
                        title={isEs ? 'Ver' : 'View'}
                        aria-label={isEs ? 'Ver' : 'View'}
                      >
                        <Eye size={14} className="text-[#9E9890]" />
                      </button>
                    )}
                    {doc.storagePath && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDownloadDocument(doc)
                        }}
                        className="p-2 rounded-lg hover:bg-[#F2F0EB] transition-colors"
                        title={isEs ? 'Descargar' : 'Download'}
                        aria-label={isEs ? 'Descargar' : 'Download'}
                      >
                        <Download size={14} className="text-[#9E9890]" />
                      </button>
                    )}
                    <button
                      onClick={async (e) => {
                        e.stopPropagation()
                        try {
                          const token = await getToken()
                          await apiFetch(`/api/projects/${projectId}/documents/${doc.id}`, {
                            method: 'DELETE',
                            headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) }
                          })
                          queryClient.invalidateQueries({ queryKey: ['documents', projectId] })
                        } catch { /* silent */ }
                      }}
                      className="p-2 rounded-lg hover:bg-red-50 transition-colors"
                      title={isEs ? 'Eliminar' : 'Delete'}
                      aria-label={isEs ? 'Eliminar' : 'Delete'}
                    >
                      <Trash2 size={14} className="text-[#9E9890] hover:text-red-500" />
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── SUB-COMPONENTS ────────────────────────────────

function SummaryCard({ label, value, icon: Icon, color, onClick, active }: {
  label: string; value: number; icon: any; color: string; onClick: () => void; active: boolean
}) {
  return (
    <button
      onClick={onClick}
      className={`text-left bg-white border rounded-xl p-3 transition-all duration-200 ${
        active ? 'border-[#06539B] ring-1 ring-[#06539B]/20 shadow-sm' : 'border-[#ECEAE4] hover:border-[#9E9890]'
      }`}
    >
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} style={{ color }} />
        <span className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890]">{label}</span>
      </div>
      <div className="text-[22px] font-[900] tracking-[-0.5px] text-[#1C1A17]">{value}</div>
    </button>
  )
}

function FilterPill({ label, active, onClick, count }: {
  label: string; active: boolean; onClick: () => void; count?: number
}) {
  return (
    <button
      onClick={onClick}
      className={`text-[10px] font-[700] px-3 py-1.5 rounded-full transition-colors ${
        active
          ? 'bg-[#06539B] text-white'
          : 'bg-white border border-[#ECEAE4] text-[#9E9890] hover:border-[#9E9890] hover:text-[#1C1A17]'
      }`}
    >
      {label}{count ? ` (${count})` : ''}
    </button>
  )
}
